import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Divider,
  Chip,
  IconButton,
} from '@mui/material'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  RemoveCircle as WithdrawIcon,
  SwapHoriz as ChangeIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { patientsAPI, interventionConfigsAPI, compositionAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

// Status indicator styles
const enrollmentStatusStyles = {
  none: { color: '#71717a', label: 'None' },
  active: { color: '#16a34a', label: 'Active' },
  paused: { color: '#ca8a04', label: 'Paused' },
  withdrawn: { color: '#dc2626', label: 'Withdrawn' },
}

const sectionLabelSx = {
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: '0.6875rem',
  mb: 1.5,
}

function EnrollmentStatusIndicator({ status }) {
  const style = enrollmentStatusStyles[status] || enrollmentStatusStyles.none
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function RuleLogEntry({ entry }) {
  const [expanded, setExpanded] = useState(false)
  const isStrategy = entry.rule_type === 'strategy'
  const rules = isStrategy
    ? entry.execution_data?.strategies || []
    : entry.execution_data?.rules || []

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.5 }}
        onClick={() => setExpanded(!expanded)}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: '1rem',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s',
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
          {new Date(entry.evaluated_at).toLocaleString()}
        </Typography>
        <Chip
          label={isStrategy ? 'Strategy' : 'Patient'}
          size="small"
          color={isStrategy ? 'primary' : 'secondary'}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.6875rem' }}
        />
        <Typography variant="caption" color="text.secondary">
          {entry.trigger_event}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {rules.filter(r => r.matched).length}/{rules.length} matched
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 3, pt: 0.5, pb: 1 }}>
          {rules.map((rule, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: rule.matched ? '#16a34a' : '#d4d4d8',
                  mt: 0.6,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                  {rule.strategy_name || rule.rule_name || `Rule #${rule.strategy_id || rule.rule_id}`}
                </Typography>
                {rule.conditions?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Conditions: {rule.conditions.map(c =>
                      `${c.type} ${c.operator || ''} ${c.value ?? ''}`
                    ).join(', ')}
                  </Typography>
                )}
                {rule.matched && rule.actions_applied?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Actions: {rule.actions_applied.map(a =>
                      `${a.type}${a.value ? `(${a.value})` : ''}${a.risk_code ? `(${a.risk_code})` : ''}`
                    ).join(', ')}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

function PatientAssignments() {
  const { hasPermission } = useAuth()
  const [patients, setPatients] = useState([])
  const [interventions, setInterventions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [saving, setSaving] = useState(false)

  // Filters
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignDialogPatient, setAssignDialogPatient] = useState(null)
  const [selectedIntervention, setSelectedIntervention] = useState('')

  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false)
  const [patientToWithdraw, setPatientToWithdraw] = useState(null)
  const [withdrawReason, setWithdrawReason] = useState('')

  // Inline detail panel state
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [trackStates, setTrackStates] = useState([])
  const [trackStatesLoading, setTrackStatesLoading] = useState(false)
  const [ruleLog, setRuleLog] = useState([])
  const [ruleLogLoading, setRuleLogLoading] = useState(false)

  const canUpdate = hasPermission('intervention_configs', 'update')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [patientsData, interventionsData] = await Promise.all([
        patientsAPI.getEnrollments(),
        interventionConfigsAPI.getAll({ status: 'active' }),
      ])
      setPatients(patientsData)
      setInterventions(interventionsData)
      setError(null)
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to load patient assignments')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const handleAssign = async () => {
    if (!assignDialogPatient || !selectedIntervention) return

    try {
      setSaving(true)
      setError(null)

      if (assignDialogPatient.current_enrollment) {
        // Change intervention
        await patientsAPI.changeIntervention(assignDialogPatient.id, {
          intervention_config_id: parseInt(selectedIntervention),
          withdrawal_reason: 'Changed to different intervention',
        })
        setSuccessMessage('Patient intervention changed successfully')
      } else {
        // New assignment
        await patientsAPI.assignToIntervention(assignDialogPatient.id, {
          intervention_config_id: parseInt(selectedIntervention),
        })
        setSuccessMessage('Patient assigned to intervention successfully')
      }

      await fetchData()
      setAssignDialogOpen(false)
      setAssignDialogPatient(null)
      setSelectedIntervention('')
      // Close detail panel since data changed
      setSelectedPatient(null)
    } catch (err) {
      console.error('Error assigning patient:', err)
      const errorMessage = err.response?.data?.error || 'Failed to assign patient to intervention'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleWithdraw = async () => {
    if (!patientToWithdraw) return

    try {
      setSaving(true)
      setError(null)

      await patientsAPI.withdrawFromIntervention(patientToWithdraw.id, withdrawReason || null)
      setSuccessMessage('Patient withdrawn from intervention')

      await fetchData()
      setWithdrawDialogOpen(false)
      setPatientToWithdraw(null)
      setWithdrawReason('')
      // Close detail panel since data changed
      setSelectedPatient(null)
    } catch (err) {
      console.error('Error withdrawing patient:', err)
      setError('Failed to withdraw patient from intervention')
    } finally {
      setSaving(false)
    }
  }

  const handleSelectPatient = (patient) => {
    if (selectedPatient?.id === patient.id) {
      // Clicking same row deselects
      setSelectedPatient(null)
      return
    }

    setSelectedPatient(patient)

    if (patient.current_enrollment) {
      setTrackStatesLoading(true)
      setRuleLogLoading(true)

      Promise.all([
        compositionAPI.getTrackStates(patient.id).catch(() => []),
        compositionAPI.getRuleLog(patient.id).catch(() => []),
      ]).then(([tracks, logs]) => {
        setTrackStates(tracks)
        setTrackStatesLoading(false)
        setRuleLog(logs)
        setRuleLogLoading(false)
      })
    } else {
      setTrackStates([])
      setRuleLog([])
    }
  }

  const openAssignDialog = (patient) => {
    setAssignDialogPatient(patient)
    setSelectedIntervention(patient.current_enrollment?.intervention_config_id?.toString() || '')
    setAssignDialogOpen(true)
  }

  const openWithdrawDialog = (patient) => {
    setPatientToWithdraw(patient)
    setWithdrawReason('')
    setWithdrawDialogOpen(true)
  }

  // Filter patients
  const filteredPatients = patients.filter(p => {
    // Status filter
    if (statusFilter === 'enrolled' && !p.current_enrollment) return false
    if (statusFilter === 'not_enrolled' && p.current_enrollment) return false

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const matchesEmail = p.email?.toLowerCase().includes(query)
      const matchesName = p.name?.toLowerCase().includes(query)
      if (!matchesEmail && !matchesName) return false
    }

    return true
  })

  const columns = [
    {
      field: 'name',
      headerName: 'Patient',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.name || 'No name'}</Typography>
          {params.row.email && (
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'current_enrollment',
      headerName: 'Current Intervention',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const enrollment = params.value
        if (!enrollment) {
          return (
            <Typography variant="body2" color="text.secondary">
              Not enrolled
            </Typography>
          )
        }
        return (
          <Box>
            <Typography variant="body2">{enrollment.intervention_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              Since {new Date(enrollment.enrolled_at).toLocaleDateString()}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => {
        const enrollment = params.row.current_enrollment
        return <EnrollmentStatusIndicator status={enrollment?.status || 'none'} />
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        const actions = []

        if (!canUpdate) return actions

        if (params.row.current_enrollment) {
          actions.push(
            <GridActionsCellItem
              icon={<Tooltip title="Change Intervention"><ChangeIcon /></Tooltip>}
              label="Change"
              onClick={() => openAssignDialog(params.row)}
              disabled={saving}
            />
          )
          actions.push(
            <GridActionsCellItem
              icon={<Tooltip title="Withdraw"><WithdrawIcon /></Tooltip>}
              label="Withdraw"
              onClick={() => openWithdrawDialog(params.row)}
              disabled={saving}
            />
          )
        } else {
          actions.push(
            <GridActionsCellItem
              icon={<Tooltip title="Assign"><AddIcon /></Tooltip>}
              label="Assign"
              onClick={() => openAssignDialog(params.row)}
              disabled={saving}
            />
          )
        }

        return actions
      },
    },
  ]

  // Group rule log entries by date
  const groupedRuleLog = ruleLog.reduce((groups, entry) => {
    const date = new Date(entry.evaluated_at).toLocaleDateString()
    if (!groups[date]) groups[date] = []
    groups[date].push(entry)
    return groups
  }, {})

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Membership</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage patient enrollment in intervention programs. Click a row to view patient details.
          </Typography>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search patients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Enrollment Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Enrollment Status"
          >
            <MenuItem value="all">All Patients</MenuItem>
            <MenuItem value="enrolled">Enrolled</MenuItem>
            <MenuItem value="not_enrolled">Not Enrolled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Data Grid */}
      <Paper>
        <DataGrid
          rows={filteredPatients}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50]}
          autoHeight
          disableSelectionOnClick
          getRowId={(row) => row.id}
          onRowClick={(params) => handleSelectPatient(params.row)}
          selectionModel={selectedPatient ? [selectedPatient.id] : []}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              minHeight: '36px !important',
              maxHeight: '36px !important',
              lineHeight: '36px !important',
              fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnHeader': {
              height: '36px !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'action.selected',
            },
            '& .MuiDataGrid-footerContainer': {
              minHeight: '32px !important',
              maxHeight: '32px !important',
              borderTop: 'none',
            },
            '& .MuiTablePagination-root': {
              overflow: 'hidden',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiTablePagination-displayedRows': {
              display: 'none !important',
            },
            '& .MuiTablePagination-actions': {
              ml: 0,
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      </Paper>

      {/* Hint text when no patient selected */}
      {!selectedPatient && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Click a row to view patient details
        </Typography>
      )}

      {/* Inline detail panel */}
      <Collapse in={selectedPatient !== null}>
        {selectedPatient && (
          <Paper sx={{ mt: 2, p: 2 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {selectedPatient.name || selectedPatient.email}
                </Typography>
                <EnrollmentStatusIndicator status={selectedPatient.current_enrollment?.status || 'none'} />
                {selectedPatient.current_enrollment && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedPatient.current_enrollment.intervention_name}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={() => setSelectedPatient(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {!selectedPatient.current_enrollment ? (
              <Typography color="text.secondary">Not enrolled in any intervention.</Typography>
            ) : (
              <>
                {/* PATHWAY STATES section */}
                <Typography variant="subtitle2" color="text.secondary" sx={sectionLabelSx}>
                  Pathway States
                </Typography>

                {trackStatesLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : trackStates.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No pathway states found for this patient.
                  </Typography>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    {trackStates.map((ts, idx) => (
                      <Accordion key={ts.id || idx} defaultExpanded={false}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <Typography variant="subtitle1" fontWeight="medium">
                              {ts.track_name || ts.track_code}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {ts.track_type}
                            </Typography>
                            {ts.status && (
                              <EnrollmentStatusIndicator status={ts.status} />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="caption" color="text.secondary" gutterBottom component="div">
                            track_context
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              bgcolor: 'grey.50',
                              p: 2,
                              borderRadius: 1,
                              overflow: 'auto',
                              maxHeight: 400,
                              fontSize: '0.8rem',
                              m: 0,
                            }}
                          >
                            {JSON.stringify(ts.track_context || {}, null, 2)}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* RULE EXECUTION LOG section */}
                <Typography variant="subtitle2" color="text.secondary" sx={sectionLabelSx}>
                  Rule Execution Log
                </Typography>

                {ruleLogLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : ruleLog.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No rule executions found in the last 30 days
                  </Typography>
                ) : (
                  Object.entries(groupedRuleLog).map(([date, entries]) => (
                    <Box key={date} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {date}
                      </Typography>
                      {entries.map((entry) => (
                        <RuleLogEntry key={entry.id} entry={entry} />
                      ))}
                    </Box>
                  ))
                )}
              </>
            )}
          </Paper>
        )}
      </Collapse>

      {/* Assign/Change Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {assignDialogPatient?.current_enrollment
            ? 'Change Intervention Assignment'
            : 'Assign to Intervention'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <Typography variant="body2">
              Patient: <strong>{assignDialogPatient?.name || assignDialogPatient?.email}</strong>
            </Typography>

            {assignDialogPatient?.current_enrollment && (
              <Alert severity="warning">
                This patient is currently enrolled in &quot;{assignDialogPatient.current_enrollment.intervention_name}&quot;.
                Assigning to a new intervention will withdraw them from the current one.
              </Alert>
            )}

            <FormControl fullWidth>
              <Typography variant="subtitle2" gutterBottom>
                Select Intervention
              </Typography>
              <RadioGroup
                value={selectedIntervention}
                onChange={(e) => setSelectedIntervention(e.target.value)}
              >
                {interventions.map((intervention) => (
                  <Paper
                    key={intervention.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: 'pointer',
                      borderColor: selectedIntervention === intervention.id.toString()
                        ? 'primary.main'
                        : 'divider',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                    onClick={() => setSelectedIntervention(intervention.id.toString())}
                  >
                    <FormControlLabel
                      value={intervention.id.toString()}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">{intervention.name}</Typography>
                          {intervention.target_population && (
                            <Typography variant="caption" color="text.secondary">
                              Target: {intervention.target_population}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>

            {interventions.length === 0 && (
              <Alert severity="info">
                No active interventions available. Create an intervention and set its status to &quot;Active&quot; first.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            disabled={!selectedIntervention || saving}
          >
            {saving ? 'Processing...' : (assignDialogPatient?.current_enrollment ? 'Change Intervention' : 'Assign Patient')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawDialogOpen} onClose={() => setWithdrawDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Withdraw from Intervention</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <Typography>
              Are you sure you want to withdraw <strong>{patientToWithdraw?.name || patientToWithdraw?.email}</strong> from{' '}
              <strong>{patientToWithdraw?.current_enrollment?.intervention_name}</strong>?
            </Typography>
            <TextField
              label="Withdrawal Reason (optional)"
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Please provide a reason for withdrawal..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleWithdraw}
            variant="contained"
            color="error"
            disabled={saving}
          >
            {saving ? 'Processing...' : 'Withdraw Patient'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PatientAssignments
